import { FilterList } from '@mui/icons-material'
import { Box, ClickAwayListener, TextField, Typography, styled } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IProductCategoryDropDown } from 'src/api/product-category'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DrawingFilters } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  BRIDLE_LEG_OPTIONS,
  EMPTY_DRAWING_FILTERS,
  countActiveFilters,
  setAvailability,
  setBridleLegCount,
  setComponentCount,
  splitFilterCategories,
} from './drawingFilters.action'

const DEFAULT_CATEGORY_ID = '1'

interface ConfigureDrawingFiltersProps {
  search: string
  onSearch: (value: string) => void
  filters: DrawingFilters
  onFilters: (filters: DrawingFilters) => void
  categories: IProductCategoryDropDown[]
  showBridleLegFilter: boolean
  resultCount: number
}

const ConfigureDrawingFilters: React.FC<ConfigureDrawingFiltersProps> = ({
  search,
  onSearch,
  filters,
  onFilters,
  categories,
  showBridleLegFilter,
  resultCount,
}) => {
  const { t } = useTranslation(['position', 'common'])
  const [open, setOpen] = useState(false)
  const { countCategories, existCategories } = splitFilterCategories(categories)
  const activeCount = countActiveFilters(filters)
  const hasFilters = showBridleLegFilter || categories.length > 0
  const categoryName = (category: IProductCategoryDropDown) =>
    String(category.id) === DEFAULT_CATEGORY_ID ? t('default-product-category-name', { ns: 'position' }) : category.name

  return (
    <FiltersRoot>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <SearchField
              fullWidth
              size="small"
              value={search}
              onChange={event => onSearch(event.target.value)}
              placeholder={t('configure-search-drawing', { ns: 'position' })}
              inputProps={{ 'aria-label': t('configure-search-drawing', { ns: 'position' }) }}
            />
            {hasFilters && (
              <FilterToggle
                type="button"
                active={open || activeCount > 0}
                aria-expanded={open}
                onClick={() => setOpen(!open)}
              >
                <FilterList sx={{ fontSize: 18 }} />
                {t('configure-filters', { ns: 'position' })}
                {activeCount > 0 && <CountBadge>{activeCount}</CountBadge>}
              </FilterToggle>
            )}
          </Box>
          {open && hasFilters && (
            <FilterDropdown>
              {showBridleLegFilter && (
                <FilterGroup title={t('bridleLegCount', { ns: 'position' })}>
                  {BRIDLE_LEG_OPTIONS.map(legCount => (
                    <FilterChip
                      key={legCount}
                      type="button"
                      active={filters.bridleLegCount === legCount}
                      aria-pressed={filters.bridleLegCount === legCount}
                      onClick={() => onFilters(setBridleLegCount(filters, legCount))}
                    >
                      {legCount}
                    </FilterChip>
                  ))}
                </FilterGroup>
              )}
              {countCategories.length > 0 && (
                <FilterGroup title={t('configure-filter-count', { ns: 'position' })}>
                  <CountGrid>
                    {countCategories.map(category => (
                      <CountField
                        key={category.id}
                        size="small"
                        type="number"
                        label={categoryName(category)}
                        value={filters.componentCounts[String(category.id)] ?? ''}
                        onChange={event =>
                          onFilters(setComponentCount(filters, String(category.id), event.target.value))
                        }
                        inputProps={{ min: 0 }}
                      />
                    ))}
                  </CountGrid>
                </FilterGroup>
              )}
              {existCategories.length > 0 && (
                <FilterGroup title={t('configure-filter-availability', { ns: 'position' })}>
                  {existCategories.map(category => {
                    const required = !!filters.availability[String(category.id)]
                    return (
                      <FilterChip
                        key={category.id}
                        type="button"
                        active={required}
                        aria-pressed={required}
                        onClick={() => onFilters(setAvailability(filters, String(category.id), !required))}
                      >
                        {category.name}
                      </FilterChip>
                    )
                  })}
                </FilterGroup>
              )}
              <DropdownFooter>
                <GridActionButton
                  disabled={activeCount === 0}
                  onClick={() => onFilters(EMPTY_DRAWING_FILTERS)}
                  buttonText={t('clear-all', { ns: 'common' })}
                  buttonTextProps={{ fontSize: 12 }}
                  border={0}
                  shadowWeight={null}
                />
                <FilterChip type="button" active onClick={() => setOpen(false)}>
                  {t('configure-show-results', { ns: 'position', count: resultCount })}
                </FilterChip>
              </DropdownFooter>
            </FilterDropdown>
          )}
        </Box>
      </ClickAwayListener>
      {(activeCount > 0 || search.trim()) && (
        <MutedText>
          {resultCount} {t('found', { ns: 'common' })}
        </MutedText>
      )}
    </FiltersRoot>
  )
}

const FilterGroup = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <MutedText sx={{ fontWeight: 600 }}>{title}</MutedText>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{children}</Box>
    </Box>
  )
}

export default ConfigureDrawingFilters

const FiltersRoot = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5, 0),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': { height: 40, fontSize: 13, fontWeight: 500 },
  '& fieldset': { borderColor: getThemeColor(theme, MUIThemeColors.inputBorder) },
}))

const FilterDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: theme.spacing(0.75),
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  maxHeight: 360,
  overflow: 'auto',
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: 6,
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  boxShadow: '0 8px 24px rgba(0,0,0,.18)',
}))

const CountGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1),
  width: '100%',
}))

const CountField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root, & .MuiInputBase-input': { fontSize: 13 },
  '& fieldset': { borderColor: getThemeColor(theme, MUIThemeColors.inputBorder) },
}))

const DropdownFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}))

const MutedText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 500,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))

const FilterChip = styled('button', { shouldForwardProp: prop => prop !== 'active' })<{ active?: boolean }>(
  ({ theme, active }) => {
    const primary = getThemeColor(theme, MUIThemeColors.primaryMain)
    return {
      minHeight: 30,
      padding: '4px 12px',
      border: `1px solid ${active ? primary : getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
      borderRadius: 4,
      background: active ? primary : getThemeColor(theme, MUIThemeColors.white),
      color: active ? getThemeColor(theme, MUIThemeColors.white) : primary,
      fontFamily: 'inherit',
      fontWeight: 600,
      fontSize: 12,
      cursor: 'pointer',
      '&:hover': {
        background: active
          ? getThemeColor(theme, MUIThemeColors.primaryDark)
          : getThemeColor(theme, MUIThemeColors.backgroundMain),
      },
    }
  },
)

const FilterToggle = styled(FilterChip)({
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 40,
  fontSize: 13,
})

const CountBadge = styled('span')({
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 9,
  background: 'rgba(255,255,255,.28)',
  fontSize: 11,
  lineHeight: '18px',
  textAlign: 'center',
})
